import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Grid, Paper, Snackbar, Alert,
  CircularProgress, Autocomplete, Tabs, Tab, Chip, Table, TableHead,
  TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Stack, Divider, MenuItem,
  ToggleButton, ToggleButtonGroup, Slider, Badge, Switch, FormControlLabel,
  LinearProgress, Accordion, AccordionSummary, AccordionDetails, Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SaveIcon from '@mui/icons-material/Save';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import NightlightIcon from '@mui/icons-material/Nightlight';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CalculateIcon from '@mui/icons-material/Calculate';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getDocument, updateDocument, getCollection } from '../services/firestoreService';
import { syncEgresoHospitalario } from '../services/egresosService';
import { db } from '../firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import DispenseForm from '../components/pharmacy/DispenseForm';
import { useAuth } from '../context/AuthContext';

// ─── Design system ────────────────────────────────────────────────────────────
import { COLORS } from '../theme/constants';

const { primary: PRIMARY, teal: TEAL, warning: AMBER, danger: DANGER, success: SUCCESS, info: INFO, muted: MUTED } = COLORS;
const CRI_COLOR = '#4F46E5';
const TURNO_MAÑANA = '#EFF6FF';
const TURNO_TARDE  = '#FFF7ED';
const TURNO_NOCHE  = '#EEF2FF';

const CELL_STATES = {
  pending:  { bg: '#F1F5F9', color: MUTED,    label: '–' },
  due:      { bg: '#FEF3C7', color: '#D97706', label: '¡Ahora!' },
  done:     { bg: '#D1FAE5', color: '#059669', label: '✓' },
  skipped:  { bg: '#FEE2E2', color: DANGER,   label: '✗' },
  modified: { bg: '#DBEAFE', color: '#1D4ED8', label: '~' },
  overdue:  { bg: '#FCA5A5', color: DANGER,   label: '¡Venc!' },
  na:       { bg: '#F8FAFC', color: '#CBD5E1', label: '—' },
};

const DEFAULT_HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

const FARMACOS_COMUNES = [
  'Ampicilina','Cefalexina','Metronidazol','Maropitant','Omeprazol','Furosemida',
  'Enalapril','Prednisolona','Dexametasona','Tramadol','Meloxicam','Ketamina',
  'Morfina','Fentanilo','Dexmedetomidina','Metoclopramida','Ondansetrón',
  'Ranitidina','Sucralfato','Lactuosa','NaCl 0.9%','Ringer Lactato','Dextrosa 5%',
];

// ─── Helper functions ─────────────────────────────────────────────────────────
const detectTurno = () => {
  const h = new Date().getHours();
  if (h >= 8  && h < 16) return 'mañana';
  if (h >= 16 && h < 24) return 'tarde';
  return 'noche';
};

const fmtDateTime = (ts) => {
  if (!ts) return '–';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (ts) => {
  if (!ts) return '–';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateKey = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const isSameDay = (a, b) => formatDateKey(a) === formatDateKey(b);

const diasDesdeIngreso = (admissionAt) => {
  if (!admissionAt) return 0;
  const d = admissionAt.seconds ? new Date(admissionAt.seconds * 1000) : new Date(admissionAt);
  return Math.floor((Date.now() - d) / 86400000);
};

const getStartHour = (treatment, hours) => {
  // Prefer explicit start hour set by the veterinarian (hora de inicio / primera dosis)
  if (treatment?.horaInicio) {
    return treatment.horaInicio;
  }
  const created = treatment?.createdAt
    ? (treatment.createdAt.seconds ? new Date(treatment.createdAt.seconds * 1000) : new Date(treatment.createdAt))
    : null;
  const createdMin = created ? created.getHours() * 60 + created.getMinutes() : null;
  for (const h of hours || []) {
    const [hh, mm] = h.split(':').map(Number);
    if (createdMin == null || hh * 60 + mm >= createdMin) return h;
  }
  return (hours || [])[0] || '08:00';
};

const hourMinutesDifference = (h1, h2) => {
  const [aH, aM] = (h1 || '').split(':').map(Number);
  const [bH, bM] = (h2 || '').split(':').map(Number);
  return (aH * 60 + aM) - (bH * 60 + bM);
};

const isScheduledHour = (treatment, hour, hours, selectedDate = new Date()) => {
  if (treatment?.type === 'procedure') return false;
  const f = String(treatment?.frecuencia || '').toLowerCase().trim();
  if (!f || f === 'sos' || f === 'cri') return false;
  const start = getTreatmentStart(treatment);
  if (!start) return false;
  const [hh, mm] = (hour || '').split(':').map(Number);
  const slot = new Date(selectedDate);
  slot.setHours(hh || 0, mm || 0, 0, 0);
  if (f === 'única dosis' || f === 'unica dosis') return slot.getTime() === start.getTime();
  const m = f.match(/^c\/(\d+)h$/);
  if (!m) return false;
  const interval = parseInt(m[1], 10) * 60;
  const diff = (slot - start) / 60000;
  if (diff < 0) return false;
  if (treatment.duracionDias) {
    const end = Number(treatment.duracionDias) * 24 * 60;
    if (diff > end) return false;
  }
  return diff % interval === 0;
};

const getCellForDate = (treatment, hour, selectedDate, now) => {
  const selectedDateStr = formatDateKey(selectedDate);
  const sameDay = selectedDateStr === formatDateKey(now);
  return treatment?.cells?.[selectedDateStr]?.[hour] || (sameDay ? treatment?.cells?.[hour] : { status: 'pending' }) || { status: 'pending' };
};

const calcCellStatus = (treatment, hour, now, hours, selectedDate = now) => {
  const cell = getCellForDate(treatment, hour, selectedDate, now);
  if (cell.status && cell.status !== 'pending') return cell.status;
  if (treatment?.type === 'procedure') return 'pending';
  const f = (treatment?.frecuencia || '').toLowerCase().trim();
  if (!f || f === 'sos' || f === 'cri') return 'pending';
  if (!isScheduledHour(treatment, hour, hours, selectedDate)) return 'na';
  const [hh, mm] = hour.split(':').map(Number);
  const sched = new Date(selectedDate);
  sched.setHours(hh, mm, 0, 0);
  const diff = (now - sched) / 60000;
  if (diff >= -15 && diff <= 30) return 'due';
  if (diff > 30) return 'overdue';
  return 'pending';
};

const getDueCount = (treatments, now, hours) =>
  treatments.reduce((acc, t) =>
    acc + (hours || []).filter((h) => {
      const st = calcCellStatus(t, h, now, hours, now);
      return st === 'due' || st === 'overdue';
    }).length, 0);

// Compute the next scheduled medication administration timestamp and name.
// It evaluates each treatment's scheduled display hours (today and tomorrow),
// considering cells already done/skipped. Returns the chronologically next
// uncompleted administration (overdue or pending).
const computeNextTreatment = (treatments, hours, now) => {
  if (!hours?.length || !treatments?.length) return null;
  const candidates = [];

  for (const t of treatments) {
    if (t.type === 'procedure') continue;
    const f = (t.frecuencia || '').toLowerCase().trim();
    if (!f || f === 'sos' || f === 'cri') continue;
    const start = getStartHour(t, hours);
    if (!start) continue;

    for (const h of hours) {
      const [hh, mm] = h.split(':').map(Number);
      for (let d = 0; d <= 1; d++) {
        const ts = new Date(now);
        ts.setDate(ts.getDate() + d);
        ts.setHours(hh, mm, 0, 0);
        if (!isScheduledHour(t, h, hours, ts)) continue;
        const cell = getCellForDate(t, h, ts, now);
        if (!cell || (cell.status !== 'done' && cell.status !== 'skipped')) {
          candidates.push({ ts, name: t.name, id: t.id });
        }
      }
    }

    // Also consider the explicit start time (horaInicio) if it is in the future
    // and it is not represented by any scheduled display hour.
    if (t.horaInicio && !hours.includes(t.horaInicio)) {
      const [hh, mm] = t.horaInicio.split(':').map(Number);
      const ts = new Date(now); ts.setHours(hh, mm, 0, 0);
      if (ts >= now) {
        const cell = getCellForDate(t, t.horaInicio, ts, now);
        if (!cell || (cell.status !== 'done' && cell.status !== 'skipped')) {
          candidates.push({ ts, name: t.name, id: t.id });
        }
      }
    }
  }

  // Earliest scheduled time first (overdue before pending)
  candidates.sort((a, b) => a.ts - b.ts);
  return candidates[0] || null;
};

const fmtTime = (d) => {
  if (!d) return '–';
  const dt = d instanceof Date ? d : (d.seconds ? new Date(d.seconds * 1000) : new Date(d));
  return dt.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
};

const getTreatmentStart = (t) => {
  if (!t.createdAt) return null;
  const d = t.createdAt.seconds ? new Date(t.createdAt.seconds * 1000) : new Date(t.createdAt);
  if (t.horaInicio) {
    const [hh, mm] = t.horaInicio.split(':').map(Number);
    d.setHours(hh, mm, 0, 0);
  }
  return d;
};

const getTreatmentNext = (t, hours, now) => {
  if (t.type === 'procedure') return null;
  const f = (t.frecuencia || '').toLowerCase().trim();
  if (!f || f === 'sos' || f === 'cri' || f === 'única dosis' || f === 'unica dosis') return null;
  const start = getTreatmentStart(t) || now;
  const candidates = [];
  for (const h of hours || []) {
    const [hh, mm] = h.split(':').map(Number);
    for (let d = 0; d <= 2; d++) {
      const ts = new Date(now);
      ts.setDate(ts.getDate() + d);
      ts.setHours(hh, mm, 0, 0);
      if (ts < start) continue;
      if (!isScheduledHour(t, h, hours, ts)) continue;
      const cell = getCellForDate(t, h, ts, now);
      if (ts >= now && (!cell || (cell.status !== 'done' && cell.status !== 'skipped'))) {
        candidates.push(ts);
      }
    }
  }
  candidates.sort((a, b) => a - b);
  return candidates[0] || null;
};

const getTreatmentEnd = (t, hours, viewDate) => {
  if (t.type === 'procedure') return null;
  const start = getTreatmentStart(t);
  if (!start || !t.duracionDias) return null;
  return new Date(start.getTime() + Number(t.duracionDias) * 24 * 60 * 60 * 1000);
};

const parseNumberOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const turnoHoursBg = (hour) => {
  const h = parseInt(hour.split(':')[0], 10);
  if (h >= 8  && h < 16) return TURNO_MAÑANA;
  if (h >= 16 && h < 24) return TURNO_TARDE;
  return TURNO_NOCHE;
};

// ─── PatientHospitalizationHeader ────────────────────────────────────────────
const ESTADO_OPTIONS = [
  { key: 'estable',      label: 'Estable',        color: SUCCESS },
  { key: 'observacion',  label: 'En observación', color: AMBER },
  { key: 'critico',      label: 'Crítico',        color: DANGER },
  { key: 'recuperacion', label: 'Recuperación',   color: TEAL },
];

function PatientHospitalizationHeader({ hosp, onStateChange, onSave, saving, users, onOpenShiftNotes, onOpenDischarge }) {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [pendingState, setPendingState] = useState('');
  const [stateNote, setStateNote] = useState('');

  const dias = diasDesdeIngreso(hosp?.admissionAt);
  const estadoOpt = ESTADO_OPTIONS.find(o => o.key === hosp?.estadoPaciente) || ESTADO_OPTIONS[0];
  const ultimaAlerta = useMemo(() => {
    const notas = (hosp?.notasTurno || []).filter(n => n.alertasParaSiguienteTurno?.trim());
    if (!notas.length) return null;
    return notas.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))[0];
  }, [hosp?.notasTurno]);

  const handleEstadoClick = (key) => {
    if (key === hosp?.estadoPaciente) return;
    setPendingState(key);
    setStateNote('');
    setDlgOpen(true);
  };

  const confirmEstado = () => {
    onStateChange(pendingState, stateNote);
    setDlgOpen(false);
  };

  return (
    <Paper elevation={2} sx={{ position: 'sticky', top: 0, zIndex: 100, borderBottom: `3px solid ${PRIMARY}`, bgcolor: 'white', px: 3, py: 1.5, mb: 2 }}>
      {/* Fila 1 — identidad */}
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        <Avatar sx={{ bgcolor: PRIMARY, width: 44, height: 44, fontWeight: 700 }}>
          {(hosp?.patientName || '?')[0].toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.1}>{hosp?.patientName || '–'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {hosp?.patientSpecies} · {hosp?.patientBreed} · {hosp?.patientWeight ? `${hosp.patientWeight} kg` : '–'}
          </Typography>
        </Box>
        <Chip icon={<LocalHospitalIcon sx={{ fontSize: 14 }} />} label={hosp?.cageName || '–'} size="small" variant="outlined" />
        <Chip icon={<EventIcon sx={{ fontSize: 14 }} />} label={`Día ${dias}`} size="small"
          sx={{ bgcolor: dias > 7 ? '#FEF3C7' : 'transparent', fontWeight: 700 }} />
        {hosp?.primaryDiagnosis && (
          <Chip label={hosp.primaryDiagnosis} size="small" sx={{ bgcolor: PRIMARY, color: 'white', fontWeight: 600, maxWidth: 220 }} />
        )}
        {hosp?.alergias && (
          <Alert severity="error" sx={{ py: 0, px: 1, fontSize: '0.75rem', fontWeight: 700 }}>
            ⚠ ALÉRGICO: {hosp.alergias}
          </Alert>
        )}
        <Box sx={{ ml: 'auto' }} />
        {/* Semáforo de estado */}
        <Stack direction="row" spacing={0.5}>
          {ESTADO_OPTIONS.map(o => (
            <Button key={o.key} size="small"
              variant={hosp?.estadoPaciente === o.key ? 'contained' : 'outlined'}
              onClick={() => handleEstadoClick(o.key)}
              sx={{ minWidth: 0, px: 1.2, fontSize: '0.7rem', fontWeight: 700, borderColor: o.color, color: hosp?.estadoPaciente === o.key ? 'white' : o.color, bgcolor: hosp?.estadoPaciente === o.key ? o.color : 'transparent', '&:hover': { bgcolor: o.color, color: 'white' } }}>
              {o.label}
            </Button>
          ))}
        </Stack>
      </Stack>

      {ultimaAlerta && (
        <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, px: 1.5, fontSize: '0.75rem', fontWeight: 600, borderRadius: 1 }}>
          ⚠ Alerta del turno: {ultimaAlerta.alertasParaSiguienteTurno}
        </Alert>
      )}

      {/* Fila 2 — acciones */}
      <Stack direction="row" alignItems="center" spacing={1} mt={1} flexWrap="wrap">
        {hosp?.veterinarioResponsable && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PersonIcon sx={{ fontSize: 15, color: MUTED }} />
            <Typography variant="caption" color="text.secondary">{hosp.veterinarioResponsable}</Typography>
          </Stack>
        )}
        <Box sx={{ ml: 'auto' }} />
        <Button size="small" startIcon={<AssignmentIcon />} onClick={onOpenShiftNotes} variant="outlined" sx={{ fontSize: '0.75rem' }}>
          Nota de turno
        </Button>
        <Button size="small" startIcon={<ExitToAppIcon />} onClick={onOpenDischarge} variant="outlined" color="warning" sx={{ fontSize: '0.75rem' }}>
          Dar de alta
        </Button>
        <Button size="small" startIcon={<SaveIcon />} variant="contained" onClick={onSave} disabled={saving}
          sx={{ bgcolor: PRIMARY, fontSize: '0.75rem' }}>
          {saving ? 'Guardando…' : 'Guardar ficha'}
        </Button>
      </Stack>

      {/* Dialog cambio de estado */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cambiar estado del paciente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            Nuevo estado: <strong>{ESTADO_OPTIONS.find(o => o.key === pendingState)?.label}</strong>
          </Typography>
          <TextField label="Nota obligatoria (motivo del cambio)" fullWidth multiline rows={3}
            value={stateNote} onChange={e => setStateNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!stateNote.trim()} onClick={confirmEstado}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ─── VitalsPanel ─────────────────────────────────────────────────────────────
const MUCOSAS_OPTIONS = ['Rosadas normales','Pálidas','Cianóticas','Ictéricas','Congestionadas','Secas'];
const CONSCIENCIA_OPTIONS = ['Alerta','Deprimido','Estuporoso','Comatoso'];
const PATRON_RESPIRATORIO_OPTIONS = ['Normal','Taquipnea','Bradipnea','Apnea','Disnea','Polipnea','Ortopnea'];
const RUIDOS_RESPIRATORIOS_OPTIONS = ['Presentes','Ausentes','Disminuidos','Crepitantes','Sibilancias','Roncus','Estridor'];
const PARAM_COLORS = { FC: DANGER, FR: INFO, 'T°': AMBER, PA: CRI_COLOR, Dolor: TEAL, Glicemia: SUCCESS, PAM: INFO };

const NORMAL_RANGES = {
  perro: { FC: [60,140], FR: [10,30], 'T°': [37.5,39.2] },
  gato:  { FC: [140,220], FR: [20,30], 'T°': [38,39.2] },
};

const emptyVitals = () => ({ temperatura:'', frecuenciaCardiaca:'', frecuenciaRespiratoria:'',
  presionSistolica:'', presionDiastolica:'', spo2:'', scoreDolor:0, mucosas:'', tlc:'',
  consciencia:'', peso:'', conO2: false, glicemia:'', vga:'', patronRespiratorio:'', ruidosRespiratorios:'',
  registradoPorId:'', turno: detectTurno() });

function VitalsPanel({ hosp, users, onSave }) {
  const [form, setForm] = useState(emptyVitals());
  const [saving, setSaving] = useState(false);
  const [param, setParam] = useState('FC');
  const [weights, setWeights] = useState([]);
  const vitales = hosp?.vitalesRegistros || [];

  useEffect(() => {
    if (!hosp?.patientId) return;
    const q = query(collection(db, 'patients', hosp.patientId, 'weights'), orderBy('date', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setWeights(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [hosp?.patientId]);

  const chartData = useMemo(() => {
    const map = { FC:'frecuenciaCardiaca', FR:'frecuenciaRespiratoria', 'T°':'temperatura',
      PA:'presionSistolica', Dolor:'scoreDolor', Glicemia:'glicemia', PAM:'pam' };
    return [...vitales].sort((a,b)=>{
      const ta = a.timestamp?.seconds||0; const tb = b.timestamp?.seconds||0; return ta-tb;
    }).map(v => ({
      x: fmtDateTime(v.timestamp),
      y: parseNumberOrNull(v[map[param]]),
      por: v.registradoPor||'–',
    }));
  }, [vitales, param]);

  const lastSix = useMemo(() => [...vitales].sort((a,b)=>{
    const ta=a.timestamp?.seconds||new Date(a.timestamp||0)/1000;
    const tb=b.timestamp?.seconds||new Date(b.timestamp||0)/1000;
    return tb-ta;
  }).slice(0,6), [vitales]);

  const weightData = useMemo(() => {
    return [...weights].sort((a,b)=>{
      const ta = a.date?.seconds || 0;
      const tb = b.date?.seconds || 0;
      return ta - tb;
    }).map(w => ({
      x: fmtDate(w.date),
      y: parseNumberOrNull(w.weight),
    }));
  }, [weights]);

  const species = (hosp?.patientSpecies||'').toLowerCase().includes('feli') ? 'gato' : 'perro';
  const ranges = NORMAL_RANGES[species];

  const handleSave = async () => {
    setSaving(true);
    try {
      const pam = form.presionSistolica && form.presionDiastolica
        ? ((Number(form.presionSistolica) + 2 * Number(form.presionDiastolica)) / 3).toFixed(1)
        : null;
      const reg = { ...form, pam, id:`vit-${Date.now()}`, timestamp: new Date(),
        registradoPor: users.find(u=>u.id===form.registradoPorId)?.name||'', };
      await onSave(reg);
      setForm(emptyVitals());
    } finally { setSaving(false); }
  };

  const f = (field,val) => setForm(p=>({...p,[field]:val}));

  return (
    <Grid container spacing={2}>
      {/* Formulario */}
      <Grid
        size={{
          xs: 12,
          md: 5
        }}>
        <Paper elevation={0} sx={{ p:2, border:'1px solid #E2E8F0', borderRadius:3 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Registrar vitales</Typography>
          <Grid container spacing={1.5}>
            <Grid size={6}><TextField size="small" fullWidth label="T° (°C)" value={form.temperatura}
              onChange={e=>f('temperatura',e.target.value)} type="number"
              helperText={`Normal ${species}: ${ranges['T°'][0]}–${ranges['T°'][1]}`}/></Grid>
            <Grid size={6}><TextField size="small" fullWidth label="FC (lpm)" value={form.frecuenciaCardiaca}
              onChange={e=>f('frecuenciaCardiaca',e.target.value)} type="number"
              helperText={`Normal: ${ranges.FC[0]}–${ranges.FC[1]}`}/></Grid>
            <Grid size={6}><TextField size="small" fullWidth label="FR (rpm)" value={form.frecuenciaRespiratoria}
              onChange={e=>f('frecuenciaRespiratoria',e.target.value)} type="number"
              helperText={`Normal: ${ranges.FR[0]}–${ranges.FR[1]}`}/></Grid>
            <Grid size={3}><TextField size="small" fullWidth label="PA sis." value={form.presionSistolica}
              onChange={e=>f('presionSistolica',e.target.value)} type="number"/></Grid>
            <Grid size={3}><TextField size="small" fullWidth label="PA dia." value={form.presionDiastolica}
              onChange={e=>f('presionDiastolica',e.target.value)} type="number"/></Grid>
            <Grid size={12}>
              <FormControlLabel control={<Switch size="small" checked={form.conO2} onChange={e=>f('conO2',e.target.checked)}/>} label="Con O₂"/>
              {form.conO2 && <TextField size="small" label="SpO2 (%)" value={form.spo2} onChange={e=>f('spo2',e.target.value)} type="number" sx={{ ml:1, width:100 }}/>}
            </Grid>
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">Score de dolor (0–4)</Typography>
              <Slider value={form.scoreDolor} min={0} max={4} step={1} marks={[{value:0,label:'Sin dolor'},{value:2,label:'Moderado'},{value:4,label:'Máx'}]}
                onChange={(_,v)=>f('scoreDolor',v)} sx={{ color: DANGER }}/>
            </Grid>
            <Grid size={6}><TextField size="small" fullWidth select label="Mucosas" value={form.mucosas} onChange={e=>f('mucosas',e.target.value)}>
              {MUCOSAS_OPTIONS.map(o=><MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField></Grid>
            <Grid size={3}><TextField size="small" fullWidth label="TLC (seg)" value={form.tlc} onChange={e=>f('tlc',e.target.value)} type="number"/></Grid>
            <Grid size={3}><TextField size="small" fullWidth select label="Consciencia" value={form.consciencia} onChange={e=>f('consciencia',e.target.value)}>
              {CONSCIENCIA_OPTIONS.map(o=><MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField></Grid>
            <Grid size={3}><TextField size="small" fullWidth label="Glicemia" value={form.glicemia} onChange={e=>f('glicemia',e.target.value)} type="number"/></Grid>
            <Grid size={3}><TextField size="small" fullWidth label="VGA" value={form.vga} onChange={e=>f('vga',e.target.value)} type="number" helperText="Vol. gástrico aspirado"/></Grid>
            <Grid size={3}><TextField size="small" fullWidth select label="Patrón respiratorio" value={form.patronRespiratorio} onChange={e=>f('patronRespiratorio',e.target.value)}>
              {PATRON_RESPIRATORIO_OPTIONS.map(o=><MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField></Grid>
            <Grid size={3}><TextField size="small" fullWidth select label="Ruidos respiratorios" value={form.ruidosRespiratorios} onChange={e=>f('ruidosRespiratorios',e.target.value)}>
              {RUIDOS_RESPIRATORIOS_OPTIONS.map(o=><MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField></Grid>
            <Grid size={3}><TextField size="small" fullWidth label="PAM" value={form.presionSistolica && form.presionDiastolica ? ((Number(form.presionSistolica)+2*Number(form.presionDiastolica))/3).toFixed(1) : ''} disabled helperText="Presión arterial media"/></Grid>
            <Grid size={6}><TextField size="small" fullWidth label="Peso (kg, opcional)" value={form.peso} onChange={e=>f('peso',e.target.value)} type="number"/></Grid>
            <Grid size={6}>
              <Stack direction="row" spacing={0.5}>
                {['mañana','tarde','noche'].map(t=>(
                  <Button key={t} size="small" variant={form.turno===t?'contained':'outlined'}
                    onClick={()=>f('turno',t)} sx={{ fontSize:'0.65rem', px:1, textTransform:'capitalize' }}>{t}</Button>
                ))}
              </Stack>
            </Grid>
            <Grid size={12}><Autocomplete size="small" options={users}
              getOptionLabel={u=>`${u.name||''} ${u.lastName||''}`.trim()}
              onChange={(_,v)=>f('registradoPorId',v?.id||'')}
              renderInput={p=><TextField {...p} label="Registrado por"/>}/></Grid>
            <Grid size={12}><Button variant="contained" fullWidth onClick={handleSave} disabled={saving}
              sx={{ bgcolor: TEAL, '&:hover':{ bgcolor:'#22a08c' } }}>{saving?'Guardando…':'Registrar vitales'}</Button></Grid>
          </Grid>
        </Paper>
      </Grid>
      {/* Historial + gráfico */}
      <Grid
        size={{
          xs: 12,
          md: 7
        }}>
        <Paper elevation={0} sx={{ p:2, border:'1px solid #E2E8F0', borderRadius:3, mb:2 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="subtitle1" fontWeight={700}>Tendencia</Typography>
            <ToggleButtonGroup size="small" value={param} exclusive onChange={(_,v)=>v&&setParam(v)}>
              {Object.keys(PARAM_COLORS).map(k=><ToggleButton key={k} value={k} sx={{ px:1, py:0.3, fontSize:'0.7rem' }}>{k}</ToggleButton>)}
            </ToggleButtonGroup>
          </Stack>
          {chartData.length < 2 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py:3, textAlign:'center' }}>
              {vitales.length===0 ? 'Sin registros aún' : 'Se necesitan ≥ 2 registros para el gráfico'}
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData} margin={{ top:4, right:8, bottom:4, left:-10 }}>
                <XAxis dataKey="x" tick={{ fontSize:9 }} interval="preserveStartEnd"/>
                <YAxis tick={{ fontSize:9 }}/>
                <RechartTooltip formatter={(v,_,p)=>[`${v} — ${p.payload.por}`,param]} labelStyle={{ fontSize:10 }}/>
                {ranges[param] && <>
                  <ReferenceLine y={ranges[param][0]} stroke={MUTED} strokeDasharray="4 2" label={{ value:'Min', fontSize:8, fill:MUTED }}/>
                  <ReferenceLine y={ranges[param][1]} stroke={MUTED} strokeDasharray="4 2" label={{ value:'Max', fontSize:8, fill:MUTED }}/>
                </>}
                <Line type="monotone" dataKey="y" stroke={PARAM_COLORS[param]} dot={{ r:3 }} activeDot={{ r:5 }}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>

        {/* Tabla últimos registros */}
        <Paper elevation={0} sx={{ border:'1px solid #E2E8F0', borderRadius:3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor:'#F8FAFC' }}>
                {['Hora','Turno','T°','FC','FR','PA','SpO2','Glc','VGA','PAM','Dolor','Por'].map(h=>(
                  <TableCell key={h} sx={{ fontSize:'0.68rem', fontWeight:700, py:0.8 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lastSix.length === 0 ? (
                <TableRow><TableCell colSpan={12} align="center" sx={{ color:MUTED, fontSize:'0.75rem', py:2 }}>Sin registros</TableCell></TableRow>
              ) : lastSix.map(v=>(
                <TableRow key={v.id} hover>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{fmtDateTime(v.timestamp)}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem', textTransform:'capitalize' }}>{v.turno||'–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{v.temperatura||'–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem', color: v.frecuenciaCardiaca > ranges.FC[1] ? DANGER : 'inherit' }}>{v.frecuenciaCardiaca||'–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{v.frecuenciaRespiratoria||'–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{v.presionSistolica ? `${v.presionSistolica}/${v.presionDiastolica}` : '–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{v.spo2||'–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{v.glicemia||'–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{v.vga||'–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{v.pam||'–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>
                    <Chip size="small" label={v.scoreDolor??'–'} sx={{ fontSize:'0.65rem', bgcolor: (v.scoreDolor>=3) ? '#FEE2E2' : (v.scoreDolor>=1)?'#FEF3C7':'#D1FAE5' }}/>
                  </TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{v.registradoPor||'–'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {/* Evolución del peso */}
        <Paper elevation={0} sx={{ p:2, border:'1px solid #E2E8F0', borderRadius:3, mt:2 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Evolución del peso</Typography>
          {weightData.length < 2 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py:2, textAlign:'center' }}>
              {weightData.length===0 ? 'Sin registros de peso aún' : 'Se necesitan ≥ 2 registros para el gráfico'}
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={weightData} margin={{ top:4, right:8, bottom:4, left:-10 }}>
                <XAxis dataKey="x" tick={{ fontSize:9 }} interval="preserveStartEnd"/>
                <YAxis tick={{ fontSize:9 }}/>
                <RechartTooltip formatter={(v)=>[`${v} kg`, 'Peso']} labelStyle={{ fontSize:10 }}/>
                <Line type="monotone" dataKey="y" stroke={TEAL} dot={{ r:3 }} activeDot={{ r:5 }}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

// ─── BalancePanel ─────────────────────────────────────────────────────────────
const TIPOS_BALANCE = ['Orina','Fecas','Comida','Vómitos','Agua'];
const UNIDADES_BALANCE = ['ml','g','unidad'];
const emptyBalance = () => ({ tipo:'Orina', cantidad:'', unidad:'ml', descripcion:'', registradoPorId:'' });

function BalancePanel({ hosp, users, onSave }) {
  const [form, setForm] = useState(emptyBalance());
  const [saving, setSaving] = useState(false);
  const registros = hosp?.balanceRegistros || [];
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.cantidad.trim()) return;
    setSaving(true);
    try {
      const reg = {
        ...form,
        id: `bal-${Date.now()}`,
        timestamp: new Date(),
        cantidad: Number(form.cantidad) || 0,
        registradoPor: users.find(u => u.id === form.registradoPorId)?.name || '',
      };
      await onSave(reg);
      setForm(emptyBalance());
    } finally { setSaving(false); }
  };

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 12,
          md: 5
        }}>
        <Paper elevation={0} sx={{ p:2, border:'1px solid #E2E8F0', borderRadius:3 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Ingresos y egresos</Typography>
          <Grid container spacing={1.5}>
            <Grid size={6}>
              <TextField select fullWidth size="small" label="Tipo" value={form.tipo}
                onChange={e => { f('tipo', e.target.value); f('unidad', e.target.value === 'Fecas' || e.target.value === 'Comida' ? 'g' : 'ml'); }}>
                {TIPOS_BALANCE.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={3}>
              <TextField fullWidth size="small" label="Cantidad" type="number" value={form.cantidad} onChange={e => f('cantidad', e.target.value)}/>
            </Grid>
            <Grid size={3}>
              <TextField select fullWidth size="small" label="Unidad" value={form.unidad} onChange={e => f('unidad', e.target.value)}>
                {UNIDADES_BALANCE.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Descripción / características" value={form.descripcion} onChange={e => f('descripcion', e.target.value)}/>
            </Grid>
            <Grid size={12}>
              <Autocomplete size="small" options={users} getOptionLabel={u => `${u.name || ''} ${u.lastName || ''}`.trim()}
                onChange={(_, v) => f('registradoPorId', v?.id || '')}
                renderInput={p => <TextField {...p} label="Registrado por" size="small"/>}/>
            </Grid>
            <Grid size={12}>
              <Button fullWidth variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: INFO }}>Registrar</Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 7
        }}>
        <Paper elevation={0} sx={{ p:2, border:'1px solid #E2E8F0', borderRadius:3 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Historial</Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor:'#F8FAFC' }}>
                {['Hora','Tipo','Cantidad','Descripción','Por'].map(h => <TableCell key={h} sx={{ fontSize:'0.68rem', fontWeight:700 }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {registros.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ color:MUTED, fontSize:'0.75rem', py:2 }}>Sin registros</TableCell></TableRow>
              ) : [...registros].sort((a,b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)).slice(0,20).map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{fmtDateTime(r.timestamp)}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{r.tipo}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{r.cantidad} {r.unidad}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{r.descripcion || '–'}</TableCell>
                  <TableCell sx={{ fontSize:'0.72rem' }}>{r.registradoPor || '–'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    </Grid>
  );
}

// ─── CellMarkDialog ───────────────────────────────────────────────────────────
function CellMarkDialog({ open, treatment, hour, users, onClose }) {
  const [executedById, setExecutedById] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [notes, setNotes] = useState('');
  const [mode, setMode] = useState('done'); // 'done' | 'skip'
  const now = new Date();
  const [timeStr, setTimeStr] = useState(`${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`);

  const execUser = users.find(u=>u.id===executedById);
  const handleConfirm = () => {
    if (!executedById.trim()) return;
    if (mode==='skip' && !skipReason.trim()) return;
    onClose({
      status: mode==='done'?'done':'skipped',
      executedAt: new Date(),
      executedBy: execUser ? `${execUser.name||''} ${execUser.lastName||''}`.trim() : 'Sin especificar',
      executedByUserId: executedById,
      skipReason: mode==='skip' ? skipReason : '',
      notes,
      timeStr,
    });
  };

  return (
    <Dialog open={open} onClose={()=>onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb:1 }}>
        {treatment?.name} — {hour}
        <Typography variant="caption" display="block" color="text.secondary">{treatment?.via ? `${treatment.via} · ` : ''}{treatment?.doseMgPerKg ? `${treatment.doseMgPerKg} mg/kg` : ''}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={0.5}>
          <Autocomplete size="small" options={users}
            getOptionLabel={u=>`${u.name||''} ${u.lastName||''}`.trim()}
            onChange={(_,v)=>setExecutedById(v?.id||'')}
            renderInput={p=><TextField {...p} label="¿Quién administró? *" size="small" error={!executedById.trim()} helperText={!executedById.trim()?'Campo obligatorio':''}/>}/>
          <TextField size="small" label="Hora real" value={timeStr} onChange={e=>setTimeStr(e.target.value)} type="time"/>
          <TextField size="small" label="Notas (opcional)" value={notes} onChange={e=>setNotes(e.target.value)} multiline rows={2}/>
          {mode==='skip' && (
            <TextField size="small" label="Motivo de omisión (obligatorio)" value={skipReason}
              onChange={e=>setSkipReason(e.target.value)} multiline rows={2}
              error={!skipReason.trim()} helperText={!skipReason.trim()?'Campo obligatorio':''}/>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" color="error" onClick={()=>setMode(mode==='skip'?'done':'skip')}>
          {mode==='skip' ? 'Cancelar omisión' : 'Omitir dosis'}
        </Button>
        <Box sx={{ flexGrow:1 }}/>
        <Button onClick={()=>onClose(null)}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm}
          disabled={!executedById.trim() || (mode==='skip'&&!skipReason.trim())}
          sx={{ bgcolor: mode==='done'?SUCCESS:DANGER, '&:hover':{ bgcolor: mode==='done'?'#059669':DANGER } }}>
          {mode==='done' ? '✓ Administrado' : '✗ Omitir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ShiftNotesDialog ─────────────────────────────────────────────────────────
function ShiftNotesDialog({ open, onClose, users, onSave, initialData = null }) {
  const [form, setForm] = useState({ turno: detectTurno(), observaciones:'', eventosRelevantes:'',
    comunicacionPropietario:'', alertasParaSiguienteTurno:'', tuvoComunicacion: false, registradoPorId:'' });
  const [saving, setSaving] = useState(false);
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  useEffect(() => {
    if (open) {
      setForm({
        turno: initialData?.turno || detectTurno(),
        observaciones: initialData?.observaciones || '',
        eventosRelevantes: initialData?.eventosRelevantes || '',
        comunicacionPropietario: initialData?.comunicacionPropietario || '',
        alertasParaSiguienteTurno: initialData?.alertasParaSiguienteTurno || '',
        tuvoComunicacion: initialData?.tuvoComunicacion || false,
        registradoPorId: initialData?.registradoPorId || '',
        id: initialData?.id || null,
        timestamp: initialData?.timestamp || null,
      });
    }
  }, [open, initialData]);
  const handleSave = async () => {
    setSaving(true);
    try {
      const reg = { ...form, id: form.id || `nota-${Date.now()}`,
        timestamp: form.timestamp || new Date(),
        registradoPor: users.find(u=>u.id===form.registradoPorId)?.name||form.registradoPor||'' };
      await onSave(reg);
      onClose();
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Editar' : 'Registrar'} nota de turno</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Stack direction="row" spacing={1}>
            {['mañana','tarde','noche'].map(t=>(
              <Button key={t} size="small" variant={form.turno===t?'contained':'outlined'}
                onClick={()=>f('turno',t)} sx={{ textTransform:'capitalize' }}>{t}</Button>
            ))}
          </Stack>
          <TextField label="Observaciones del turno" multiline rows={3} value={form.observaciones} onChange={e=>f('observaciones',e.target.value)} fullWidth/>
          <TextField label="Eventos relevantes" multiline rows={2} value={form.eventosRelevantes} onChange={e=>f('eventosRelevantes',e.target.value)} fullWidth/>
          <FormControlLabel control={<Switch checked={form.tuvoComunicacion} onChange={e=>f('tuvoComunicacion',e.target.checked)}/>} label="¿Hubo comunicación con el propietario?"/>
          {form.tuvoComunicacion && <TextField label="Resumen de la comunicación" multiline rows={2} value={form.comunicacionPropietario} onChange={e=>f('comunicacionPropietario',e.target.value)} fullWidth/>}
          <TextField label="⚠ Alertas para el siguiente turno" multiline rows={2} value={form.alertasParaSiguienteTurno} onChange={e=>f('alertasParaSiguienteTurno',e.target.value)} fullWidth
            InputProps={{ sx:{ bgcolor:'#FFFBEB' } }}/>
          <Autocomplete size="small" options={users} getOptionLabel={u=>`${u.name||''} ${u.lastName||''}`.trim()}
            onChange={(_,v)=>f('registradoPorId',v?.id||'')}
            renderInput={p=><TextField {...p} label="Registrado por" size="small"/>}/>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving?'Guardando…':'Guardar nota'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── DischargeDialog ──────────────────────────────────────────────────────────
const CHECKLIST_ITEMS = [
  { key:'diagnosticoEgreso',    label:'Diagnóstico de egreso confirmado' },
  { key:'medicamentosPrescritos', label:'Medicamentos para casa prescritos' },
  { key:'instrucciones',        label:'Instrucciones de cuidado en casa' },
  { key:'restricciones',        label:'Restricciones de actividad especificadas' },
  { key:'dieta',                label:'Dieta de alta especificada' },
  { key:'control',              label:'Control de seguimiento agendado' },
  { key:'signosAlarma',         label:'Signos de alarma explicados al propietario' },
  { key:'propietarioConfirmo',  label:'Propietario confirmó recepción de instrucciones' },
];

function DischargeDialog({ open, onClose, onDischarge, hospitalizationId }) {
  const nav = useNavigate();
  const [checks, setChecks] = useState({});
  const [notas, setNotas] = useState({});
  const allDone = CHECKLIST_ITEMS.every(i => checks[i.key]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Protocolo de Alta</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb:2, fontSize:'0.8rem' }}>
          Para un alta completa con epicrisis, instrucciones al propietario y firma, usa el módulo de Epicrisis.
        </Alert>
        <Stack spacing={1.5} mt={1}>
          {CHECKLIST_ITEMS.map(item=>(
            <Box key={item.key}>
              <FormControlLabel
                control={<Switch checked={!!checks[item.key]} onChange={e=>setChecks(p=>({...p,[item.key]:e.target.checked}))}/>}
                label={<Typography variant="body2" fontWeight={checks[item.key]?700:400}>{item.label}</Typography>}/>
              {checks[item.key] && ['instrucciones','restricciones','dieta','control','signosAlarma'].includes(item.key) && (
                <TextField size="small" fullWidth placeholder="Detalle..." value={notas[item.key]||''}
                  onChange={e=>setNotas(p=>({...p,[item.key]:e.target.value}))} sx={{ mt:0.5, ml:4 }}/>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="outlined" color="primary"
          onClick={()=>{ onClose(); nav(`/hospital-epicrisis/${hospitalizationId}`); }}>
          📋 Epicrisis completa
        </Button>
        <Button variant="contained" color="warning" disabled={!allDone}
          onClick={()=>{ onDischarge({ checks, notas }); onClose(); }}>
          Alta rápida
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── DailySOAPDialog ──────────────────────────────────────────────────────────
function DailySOAPDialog({ open, onClose, users, onSave, initialData = null }) {
  const [form, setForm] = useState({ subjetivo:'', objetivo:'', evaluacion:'', plan:'', firmadoPorId:'' });
  const [saving, setSaving] = useState(false);
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  useEffect(() => {
    if (open) {
      setForm({
        subjetivo: initialData?.subjetivo || '',
        objetivo: initialData?.objetivo || '',
        evaluacion: initialData?.evaluacion || '',
        plan: initialData?.plan || '',
        firmadoPorId: initialData?.firmadoPorId || '',
        id: initialData?.id || null,
        fecha: initialData?.fecha || null,
        timestamp: initialData?.timestamp || null,
      });
    }
  }, [open, initialData]);
  const handleSave = async () => {
    setSaving(true);
    try {
      const isEdit = !!form.id;
      const reg = { ...form, id: form.id || `soap-${Date.now()}`,
        fecha: form.fecha || new Date(), timestamp: form.timestamp || new Date(),
        firmadoPor: users.find(u=>u.id===form.firmadoPorId)?.name||form.firmadoPor||'' };
      await onSave(reg);
      onClose();
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Editar' : 'Nuevo'} Evolución SOAP — {new Date().toLocaleDateString('es-CL',{day:'2-digit',month:'short',year:'numeric'})}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {[['subjetivo','S — Subjetivo (anamnesis del día)'],['objetivo','O — Objetivo (examen físico)'],
            ['evaluacion','A — Evaluación (mejor / igual / peor + análisis)'],['plan','P — Plan (cambios al tratamiento)']].map(([k,lbl])=>(
            <TextField key={k} label={lbl} multiline rows={3} value={form[k]} onChange={e=>f(k,e.target.value)} fullWidth/>
          ))}
          <Autocomplete size="small" options={users} getOptionLabel={u=>`${u.name||''} ${u.lastName||''}`.trim()}
            onChange={(_,v)=>f('firmadoPorId',v?.id||'')}
            renderInput={p=><TextField {...p} label="Firmado por" size="small"/>}/>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving?'Guardando…':'Guardar SOAP'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const HospitalFlowsheet = () => {
  const { hospitalizationId } = useParams();
  const navigate = useNavigate();

  const [hospitalization, setHospitalization] = useState(null);
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [treatments, setTreatments] = useState([]);
  const treatmentsRef = useRef(treatments);
  useEffect(() => { treatmentsRef.current = treatments; }, [treatments]);

  const [diagnoses, setDiagnoses] = useState([]);
  const [users, setUsers] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const { currentUser: authUser, userData: authUserData } = useAuth();
  const currentUser = useMemo(() => (authUserData && authUser ? { id: authUser.uid, ...authUserData } : null), [authUser, authUserData]);
  const medicalUsers = useMemo(() => users.filter(u => ['Médico Veterinario','Director Médico'].includes(u.role)), [users]);
  useEffect(() => {
    if (!currentUser) return;
    if (['Médico Veterinario','Director Médico'].includes(currentUser.role)) {
      setNewTreatment(p => ({ ...p, doctorId: p.doctorId || currentUser.id }));
      setNewProcedure(p => ({ ...p, doctorId: p.doctorId || currentUser.id }));
    }
  }, [currentUser]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Tab + UI state
  const [activeTab, setActiveTab] = useState(0);
  const [indicationTab, setIndicationTab] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editingType, setEditingType] = useState(null); // 'medication' | 'procedure'
  const [turnoView, setTurnoView] = useState('24h'); // '24h' | 'turno'
  const [now, setNow] = useState(new Date());
  const [viewDate, setViewDate] = useState(new Date());

  // Dialog states
  const [cellDlg, setCellDlg] = useState({ open: false, treatment: null, hour: '', date: null });
  const [shiftDlg, setShiftDlg] = useState(false);
  const [dischargeDlg, setDischargeDlg] = useState(false);
  const [soapDlg, setSoapDlg] = useState(false);
  const [editingSOAP, setEditingSOAP] = useState(null);
  const [editingShiftNote, setEditingShiftNote] = useState(null);


  const [newTreatment, setNewTreatment] = useState({ name:'', via:'IV', frecuencia:'c/8h', horaInicio:'', duracionDias:'', doseMgPerKg:'', notas:'', doctorId: (['Médico Veterinario','Director Médico'].includes(currentUser?.role) ? currentUser?.id : ''), prioridad:'rutina' });
  const [newProcedure, setNewProcedure] = useState({ procedureId:'', notes:'', doctorId: (['Médico Veterinario','Director Médico'].includes(currentUser?.role) ? currentUser?.id : '') });
  const lastNextTreatment = useRef(null);

  useEffect(() => {
    let unsubscribe = null;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [hosp, diagnosesData, usersData, proceduresData] = await Promise.all([
          getDocument('hospitalizations', hospitalizationId),
          getCollection('diagnoses'),
          getCollection('users'),
          getCollection('procedures'),
        ]);
        
        if (!hosp) {
          setError('No se encontró la hospitalización.');
          setLoading(false);
          return;
        }
        setHospitalization(hosp);
        setDiagnoses(diagnosesData);
        setUsers(usersData);
        setProcedures(proceduresData.filter(p => p.pabellon === false));

        const flowsheet = hosp.flowsheet || {};
        const loadedHours = Array.isArray(flowsheet.hours) && flowsheet.hours.length === 24 ? flowsheet.hours : DEFAULT_HOURS;
        const legacyTreatments = Array.isArray(flowsheet.treatments) ? flowsheet.treatments : [];

        setHours(loadedHours);

        const treatmentsCollection = collection(db, 'hospitalizations', hospitalizationId, 'treatments');
        const snap = await getDocs(query(treatmentsCollection));
        if (snap.empty && legacyTreatments.length > 0) {
          const batch = writeBatch(db);
          legacyTreatments.forEach((t) => {
            const ref = doc(treatmentsCollection);
            batch.set(ref, { ...t, id: ref.id });
          });
          await batch.commit();
        }

        unsubscribe = onSnapshot(query(treatmentsCollection), (snapshot) => {
          const mapped = snapshot.docs.map((d) => {
            const data = d.data();
            const cells = { ...(data.cells || {}) };
            loadedHours.forEach((h) => {
              if (!cells[h]) {
                cells[h] = { status: 'pending' };
              } else if (!cells[h].status) {
                cells[h] = { ...cells[h], status: 'pending' };
              }
            });
            return { id: d.id, ...data, cells };
          });
          setTreatments(mapped);
        });
      } catch (e) {
        console.error('Error loading flowsheet:', e);
        setError('Error al cargar la hoja de tratamiento');
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [hospitalizationId]);

  // Compute and persist next scheduled treatment timestamp (runs on treatments/hours changes and every minute via now)
  useEffect(() => {
    if (!hospitalizationId || !treatments.length) {
      // If no treatments, clear nextTreatmentAt if it was set
      if (lastNextTreatment.current) {
        lastNextTreatment.current = null;
        setHospitalization(prev => prev ? { ...prev, nextTreatmentAt: null, nextTreatmentName: null } : prev);
        updateDocument('hospitalizations', hospitalizationId, { nextTreatmentAt: null, nextTreatmentName: null, updatedAt: new Date() }).catch(() => {});
      }
      return;
    }
    const next = computeNextTreatment(treatments, hours, now);
    const nextAt = next ? next.ts : null;
    const nextName = next ? next.name : null;
    const nextAtTime = nextAt ? nextAt.getTime() : null;

    if (lastNextTreatment.current?.time !== nextAtTime || lastNextTreatment.current?.name !== nextName) {
      lastNextTreatment.current = { time: nextAtTime, name: nextName };
      setHospitalization(prev => prev ? { ...prev, nextTreatmentAt: nextAt, nextTreatmentName: nextName } : prev);
      updateDocument('hospitalizations', hospitalizationId, { nextTreatmentAt: nextAt, nextTreatmentName: nextName, updatedAt: new Date() }).catch(() => {});
    }
  }, [treatments, hours, now, hospitalizationId]);

  const handleToggleCell = useCallback((treatmentId, hour) => {
    const t = treatments.find(x => x.id === treatmentId);
    if (!t) return;
    const st = calcCellStatus(t, hour, now, hours, viewDate);
    if (st === 'na' || st === 'done' || st === 'skipped') return;
    setCellDlg({ open: true, treatment: t, hour, date: viewDate });
  }, [treatments, now, hours, viewDate]);

  const handleCellConfirm = useCallback(async (result) => {
    if (!result) { setCellDlg({ open: false, treatment: null, hour: '', date: null }); return; }
    const { treatment, hour, date } = cellDlg;
    const viewDateStr = formatDateKey(date || viewDate);
    try {
      await updateDoc(doc(db, 'hospitalizations', hospitalizationId, 'treatments', treatment.id), {
        [`cells.${viewDateStr}.${hour}`]: { ...result },
      });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al marcar la celda', severity: 'error' });
    } finally {
      setCellDlg({ open: false, treatment: null, hour: '', date: null });
    }
  }, [cellDlg, hospitalizationId, viewDate]);

  const handleSave = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'hospitalizations', hospitalizationId), {
        'flowsheet.hours': hours,
        updatedAt: new Date(),
      });
      if (!silent) setSnackbar({ open: true, message: 'Hoja de tratamiento guardada ✓', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al guardar', severity: 'error' });
    } finally { setSaving(false); }
  }, [hospitalizationId, hours]);

  // Actualizar estados 'due' cada 60 seg
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const handleSaveVitals = useCallback(async (reg) => {
    const updated = [...(hospitalization?.vitalesRegistros || []), reg];
    const nextWeight = reg.peso ? Number(reg.peso) : (hospitalization?.patientWeight || null);
    setHospitalization(p => ({ ...p, vitalesRegistros: updated, lastVitals: reg, patientWeight: nextWeight || p.patientWeight }));
    await updateDocument('hospitalizations', hospitalizationId, {
      vitalesRegistros: updated, lastVitals: reg, updatedAt: new Date(),
      patientWeight: nextWeight || hospitalization?.patientWeight || '',
    });
    // Also save to vitalSigns collection so Epicrisis can query them
    try {
      await addDoc(collection(db, 'vitalSigns'), {
        ...reg,
        hospitalizationId,
        patientId: hospitalization?.patientId || '',
        registeredAt: reg.timestamp || new Date(),
        createdAt: new Date(),
      });
    } catch (e) { console.error('No se pudo guardar en vitalSigns:', e); }
    // Save weight to patient's history
    if (reg.peso && hospitalization?.patientId) {
      try {
        const w = Number(reg.peso);
        await addDoc(collection(db, 'patients', hospitalization.patientId, 'weights'), {
          date: new Date(),
          weight: w,
          source: 'hospitalization',
          hospitalizationId,
        });
        await updateDoc(doc(db, 'patients', hospitalization.patientId), { weight: w, updatedAt: new Date() });
      } catch (e) { console.error('No se pudo guardar peso del paciente:', e); }
    }
    setSnackbar({ open: true, message: 'Vitales registrados ✓', severity: 'success' });
  }, [hospitalization, hospitalizationId]);

  const handleSaveBalance = useCallback(async (reg) => {
    const updated = [...(hospitalization?.balanceRegistros || []), reg];
    setHospitalization(p => ({ ...p, balanceRegistros: updated }));
    await updateDocument('hospitalizations', hospitalizationId, { balanceRegistros: updated, updatedAt: new Date() });
    setSnackbar({ open: true, message: 'Registro de ingreso/egreso guardado ✓', severity: 'success' });
  }, [hospitalization, hospitalizationId]);

  const handleSaveShiftNote = useCallback(async (reg) => {
    // If reg has an existing id, it's an edit; otherwise it's a new note
    const existing = hospitalization?.notasTurno || [];
    let updated;
    if (reg.id && existing.some(n => n.id === reg.id)) {
      updated = existing.map(n => n.id === reg.id ? { ...reg } : n);
    } else {
      updated = [...existing, reg];
    }
    setHospitalization(p => ({ ...p, notasTurno: updated }));
    await updateDocument('hospitalizations', hospitalizationId, { notasTurno: updated, updatedAt: new Date() });
    setSnackbar({ open: true, message: 'Nota de turno guardada ✓', severity: 'success' });
  }, [hospitalization, hospitalizationId]);

  const handleDeleteShiftNote = useCallback(async (noteId) => {
    const updated = (hospitalization?.notasTurno || []).filter(n => n.id !== noteId);
    setHospitalization(p => ({ ...p, notasTurno: updated }));
    await updateDocument('hospitalizations', hospitalizationId, { notasTurno: updated, updatedAt: new Date() });
    setSnackbar({ open: true, message: 'Nota de turno eliminada', severity: 'info' });
  }, [hospitalization, hospitalizationId]);

  const handleSaveSOAP = useCallback(async (reg) => {
    // If reg has an existing id, it's an edit; otherwise it's new
    const existing = hospitalization?.soapDiario || [];
    let updated;
    if (reg.id && existing.some(s => s.id === reg.id)) {
      updated = existing.map(s => s.id === reg.id ? { ...reg } : s);
    } else {
      updated = [...existing, reg];
    }
    setHospitalization(p => ({ ...p, soapDiario: updated }));
    await updateDocument('hospitalizations', hospitalizationId, { soapDiario: updated, updatedAt: new Date() });
    setSnackbar({ open: true, message: 'SOAP guardado ✓', severity: 'success' });
  }, [hospitalization, hospitalizationId]);

  const handleDeleteSOAP = useCallback(async (soapId) => {
    const updated = (hospitalization?.soapDiario || []).filter(s => s.id !== soapId);
    setHospitalization(p => ({ ...p, soapDiario: updated }));
    await updateDocument('hospitalizations', hospitalizationId, { soapDiario: updated, updatedAt: new Date() });
    setSnackbar({ open: true, message: 'SOAP eliminado', severity: 'info' });
  }, [hospitalization, hospitalizationId]);

  const handleStateChange = useCallback(async (newState, nota) => {
    const prev = hospitalization?.estadoPaciente || 'estable';
    const historialEntry = {
      id: `est-${Date.now()}`,
      from: prev,
      to: newState,
      nota,
      changedAt: new Date(),
      changedBy: currentUser ? `${currentUser.name||''} ${currentUser.lastName||''}`.trim() : '',
      changedById: currentUser?.id || '',
    };
    const historial = [...(hospitalization?.estadoHistorial || []), historialEntry];
    setHospitalization(p => ({ ...p, estadoPaciente: newState, estadoHistorial: historial }));
    await updateDocument('hospitalizations', hospitalizationId, {
      estadoPaciente: newState, estadoNota: nota, estadoCambiadoAt: new Date(),
      estadoHistorial: historial, updatedAt: new Date(),
    });
    setSnackbar({ open: true, message: `Estado cambiado a ${newState}`, severity: 'info' });
  }, [hospitalizationId, hospitalization, currentUser]);

  const handleDischarge = useCallback(async (data) => {
    const altaAt = new Date();
    await updateDocument('hospitalizations', hospitalizationId, {
      status: 'alta', checklistAlta: data, altaAt, updatedAt: altaAt,
    });
    if (hospitalization?.patientId) {
      await updateDocument('patients', hospitalization.patientId, {
        status: 'activo',
        updatedAt: altaAt,
      });
    }
    await syncEgresoHospitalario({
      hospitalization: { id: hospitalizationId, ...hospitalization },
      altaAt,
    }).catch(err => console.error('Error sincronizando egreso:', err));
    setSnackbar({ open: true, message: 'Alta registrada. Redirigiendo…', severity: 'success' });
    setTimeout(() => navigate('/hospital'), 2000);
  }, [hospitalizationId, hospitalization, navigate]);

  const handleNewTreatmentChange = (e) => {
    const { name, value } = e.target;
    setNewTreatment(prev => ({ ...prev, [name]: value }));
  };

  const handleNewProcedureChange = (field, value) => {
    setNewProcedure(prev => ({ ...prev, [field]: value }));
  };

  const handleEditClick = (t) => {
    setEditingId(t.id);
    setEditingType(t.type);
    if (t.type === 'medication') {
      setIndicationTab(0);
      setNewTreatment({
        name: t.name || '',
        via: t.via || 'IV',
        frecuencia: t.frecuencia || 'c/8h',
        horaInicio: t.horaInicio || '',
        duracionDias: t.duracionDias != null ? String(t.duracionDias) : '',
        doseMgPerKg: t.doseMgPerKg ?? '',
        notas: t.notas || '',
        doctorId: t.doctorId || '',
        prioridad: t.prioridad || 'rutina',
      });
    } else {
      setIndicationTab(1);
      setNewProcedure({
        procedureId: t.procedureId || '',
        notes: t.notes || '',
        doctorId: t.doctorId || '',
      });
    }
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm('¿Eliminar esta indicación?')) return;
    try {
      await deleteDoc(doc(db, 'hospitalizations', hospitalizationId, 'treatments', id));
      if (editingId === id) {
        setEditingId(null);
        setEditingType(null);
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al eliminar la indicación', severity: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingType(null);
    setNewTreatment({ name:'', via:'IV', frecuencia:'c/8h', horaInicio:'', duracionDias:'', doseMgPerKg:'', notas:'', doctorId: (['Médico Veterinario','Director Médico'].includes(currentUser?.role) ? currentUser?.id : ''), prioridad:'rutina' });
    setNewProcedure({ procedureId:'', notes:'', doctorId: (['Médico Veterinario','Director Médico'].includes(currentUser?.role) ? currentUser?.id : '') });
  };

  const handleAddTreatment = async () => {
    if (!newTreatment.name.trim()) {
      setSnackbar({ open: true, message: 'El nombre del tratamiento es obligatorio', severity: 'warning' }); return;
    }
    const doseMgPerKg = parseNumberOrNull(newTreatment.doseMgPerKg);
    const weight = parseNumberOrNull(hospitalization?.patientWeight);
    const calculatedDoseMg = doseMgPerKg != null && weight != null ? doseMgPerKg * weight : null;
    const doctorId = newTreatment.doctorId || (['Médico Veterinario', 'Director Médico'].includes(currentUser?.role) ? currentUser?.id : null);
    const doctor = users.find(u => u.id === doctorId) || currentUser;

    if (editingId && editingType === 'medication') {
      try {
        await updateDoc(doc(db, 'hospitalizations', hospitalizationId, 'treatments', editingId), {
          name: newTreatment.name.trim(),
          doseMgPerKg,
          calculatedDoseMg,
          via: newTreatment.via,
          frecuencia: newTreatment.frecuencia,
          horaInicio: newTreatment.horaInicio || null,
          duracionDias: newTreatment.duracionDias ? Number(newTreatment.duracionDias) : null,
          prioridad: newTreatment.prioridad,
          notas: newTreatment.notas,
          doctorId,
          doctorName: doctor ? `${doctor.name} ${doctor.lastName||''}`.trim() : '–',
        });
        setEditingId(null);
        setEditingType(null);
        setNewTreatment({ name:'', via:'IV', frecuencia:'c/8h', horaInicio:'', duracionDias:'', doseMgPerKg:'', notas:'', doctorId: (['Médico Veterinario','Director Médico'].includes(currentUser?.role) ? currentUser?.id : ''), prioridad:'rutina' });
      } catch (e) {
        setSnackbar({ open: true, message: 'Error al actualizar el tratamiento', severity: 'error' });
      }
      return;
    }

    const cells = {};
    hours.forEach(h => { cells[h] = { status: 'pending' }; });
    const newT = {
      name: newTreatment.name.trim(), doseMgPerKg, calculatedDoseMg,
      via: newTreatment.via, frecuencia: newTreatment.frecuencia,
      horaInicio: newTreatment.horaInicio || null,
      duracionDias: newTreatment.duracionDias ? Number(newTreatment.duracionDias) : null,
      prioridad: newTreatment.prioridad,
      notas: newTreatment.notas, doctorId, doctorName: doctor ? `${doctor.name} ${doctor.lastName||''}`.trim() : '–',
      type: 'medication', createdAt: new Date(), cells,
    };
    try {
      await addDoc(collection(db, 'hospitalizations', hospitalizationId, 'treatments'), newT);
      setNewTreatment({ name:'', via:'IV', frecuencia:'c/8h', horaInicio:'', duracionDias:'', doseMgPerKg:'', notas:'', doctorId: (['Médico Veterinario','Director Médico'].includes(currentUser?.role) ? currentUser?.id : ''), prioridad:'rutina' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al agregar el tratamiento', severity: 'error' });
    }
  };

  const handleAddProcedure = async () => {
    if (!newProcedure.procedureId) {
      setSnackbar({ open: true, message: 'Debe seleccionar un procedimiento', severity: 'warning' }); return;
    }
    const procedure = procedures.find(p => p.id === newProcedure.procedureId);
    const doctorId = newProcedure.doctorId || (['Médico Veterinario', 'Director Médico'].includes(currentUser?.role) ? currentUser?.id : null);
    const doctor = users.find(u => u.id === doctorId) || currentUser;

    if (editingId && editingType === 'procedure') {
      try {
        await updateDoc(doc(db, 'hospitalizations', hospitalizationId, 'treatments', editingId), {
          name: procedure?.procedure || '',
          procedureId: newProcedure.procedureId,
          procedureCode: procedure?.code,
          notes: newProcedure.notes.trim(),
          doctorId,
          doctorName: doctor ? `${doctor.name} ${doctor.lastName||''}`.trim() : '–',
        });
        setEditingId(null);
        setEditingType(null);
        setNewProcedure({ procedureId:'', notes:'', doctorId: (['Médico Veterinario','Director Médico'].includes(currentUser?.role) ? currentUser?.id : '') });
      } catch (e) {
        setSnackbar({ open: true, message: 'Error al actualizar el procedimiento', severity: 'error' });
      }
      return;
    }

    const cells = {};
    hours.forEach(h => { cells[h] = { status: 'pending' }; });
    const newT = {
      name: procedure?.procedure||'', procedureId: procedure?.id,
      procedureCode: procedure?.code, notes: newProcedure.notes.trim(),
      doctorId, doctorName: doctor ? `${doctor.name} ${doctor.lastName||''}`.trim() : '–',
      type: 'procedure', createdAt: new Date(), cells,
    };
    try {
      await addDoc(collection(db, 'hospitalizations', hospitalizationId, 'treatments'), newT);
      setNewProcedure({ procedureId:'', notes:'', doctorId: (['Médico Veterinario','Director Médico'].includes(currentUser?.role) ? currentUser?.id : '') });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al agregar el procedimiento', severity: 'error' });
    }
  };

  const handlePrimaryDiagnosisChange = (_, v) => {
    if (v && !hospitalization.primaryDiagnosisId)
      setHospitalization(p => ({ ...p, primaryDiagnosisId: v.id, primaryDiagnosis: v.diagnosis }));
  };
  const handleSecondaryDiagnosisChange = (_, v) => {
    if (!v) return;
    const ids = hospitalization.secondaryDiagnosisIds || [];
    if (!ids.includes(v.id))
      setHospitalization(p => ({ ...p, secondaryDiagnosisIds: [...ids, v.id], secondaryDiagnoses: [...(p.secondaryDiagnoses||[]), v.diagnosis] }));
  };
  const handleRemovePrimaryDiagnosis = () =>
    setHospitalization(p => ({ ...p, primaryDiagnosisId: null, primaryDiagnosis: '' }));
  const handleRemoveSecondaryDiagnosis = (idx) =>
    setHospitalization(p => ({
      ...p,
      secondaryDiagnosisIds: p.secondaryDiagnosisIds.filter((_,i) => i !== idx),
      secondaryDiagnoses: p.secondaryDiagnoses.filter((_,i) => i !== idx),
    }));

  const viewDateStr = useMemo(() => formatDateKey(viewDate), [viewDate]);
  const isTodayView = useMemo(() => isSameDay(viewDate, now), [viewDate, now]);
  const nextTto = useMemo(() => computeNextTreatment(treatments, hours, now), [treatments, hours, now]);

  const dueCount = useMemo(() => getDueCount(treatments, now, hours), [treatments, now, hours]);
  const overdueTreatments = useMemo(() => {
    return treatments.filter(t => {
      return (hours || []).some((h) => {
        const st = calcCellStatus(t, h, now, hours, now);
        return st === 'overdue';
      });
    });
  }, [treatments, now, hours]);
  const overdueCount = overdueTreatments.length;
  const visibleHours = useMemo(() => {
    if (turnoView !== 'turno' || !isTodayView) return hours;
    const t = detectTurno();
    return hours.filter(h => {
      const hr = parseInt(h,10);
      return t==='mañana'?(hr>=8&&hr<16):t==='tarde'?(hr>=16&&hr<24):(hr<8||hr>=24);
    });
  }, [turnoView, hours, isTodayView]);

  if (loading) return <Box sx={{ p:3, display:'flex', justifyContent:'center' }}><CircularProgress /></Box>;
  if (!hospitalization) return <Box sx={{ p:3 }}><Alert severity="error">{error || 'No se encontró la hospitalización.'}</Alert></Box>;

  const weightNumber = parseNumberOrNull(hospitalization.patientWeight);

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', pb: 4 }}>
      {/* Header sticky */}
      <PatientHospitalizationHeader
        hosp={hospitalization}
        onStateChange={handleStateChange}
        onSave={() => handleSave(false)}
        saving={saving}
        users={users}
        onOpenShiftNotes={() => setShiftDlg(true)}
        onOpenDischarge={() => setDischargeDlg(true)}
      />
      {overdueCount > 0 && (
        <Alert severity="error" sx={{ mx: 3, mb: 2, fontWeight: 600 }}
          action={<Button size="small" color="inherit" onClick={() => setActiveTab(1)}>Ir a tratamientos</Button>}>
          ⚠ {overdueCount} tratamiento(s) vencido(s): {overdueTreatments.slice(0,3).map(t => t.name).join(', ')}{overdueTreatments.length > 3 ? '...' : ''}
        </Alert>
      )}
      <Box sx={{ px: 3 }}>
        {/* Tabs principales */}
        <Tabs value={activeTab} onChange={(_,v) => setActiveTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '2px solid #E2E8F0', mb: 3, bgcolor: 'white', borderRadius: 2, px: 1 }}>
          <Tab icon={<MonitorHeartIcon />} label="Vitales" iconPosition="start"/>
          <Tab icon={<MedicalServicesIcon />} iconPosition="start"
            label={<Badge badgeContent={dueCount||null} color="error">Tratamientos</Badge>}/>
          <Tab icon={<AssignmentIcon />} label="Diagnósticos" iconPosition="start"/>
          <Tab icon={<StickyNote2Icon />} label="Evolución SOAP" iconPosition="start"/>
          <Tab icon={<ExitToAppIcon />} label="Alta" iconPosition="start"/>
          <Tab icon={<StickyNote2Icon />} label="Notas de turno" iconPosition="start"/>
          <Tab icon={<AssignmentIcon />} label="Balance" iconPosition="start"/>
        </Tabs>

        {/* ── TAB 0: VITALES ── */}
        {activeTab === 0 && (
          <VitalsPanel hosp={hospitalization} users={medicalUsers} onSave={handleSaveVitals}/>
        )}

        {/* ── TAB 1: TRATAMIENTOS ── */}
        {activeTab === 1 && (
          <Box>
            {/* Formulario agregar indicación */}
            <Paper elevation={0} sx={{ p:2.5, mb:2, border:'1px solid #E2E8F0', borderRadius:3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>Agregar indicación</Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant={turnoView==='turno'?'contained':'outlined'}
                    onClick={()=>setTurnoView('turno')} sx={{ fontSize:'0.72rem' }}>Mi turno</Button>
                  <Button size="small" variant={turnoView==='24h'?'contained':'outlined'}
                    onClick={()=>setTurnoView('24h')} sx={{ fontSize:'0.72rem' }}>24 horas</Button>
                </Stack>
              </Stack>
              <Tabs value={indicationTab} onChange={(_,v)=>{ if (editingId) return; setIndicationTab(v); }} sx={{ mb:2 }}>
                <Tab label="Medicamentos" disabled={!!editingId} />
                <Tab label="Procedimientos" disabled={!!editingId} />
                <Tab label="Farmacia" disabled={!!editingId} />
              </Tabs>

              {indicationTab === 0 && (
                <Grid container spacing={1.5}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 4
                    }}>
                    <Autocomplete freeSolo options={FARMACOS_COMUNES} value={newTreatment.name}
                      onInputChange={(_,v)=>setNewTreatment(p=>({...p,name:v}))}
                      renderInput={p=><TextField {...p} size="small" label="Medicamento" name="name"/>}/>
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 2
                    }}>
                    <TextField size="small" fullWidth select label="Vía" name="via"
                      value={newTreatment.via} onChange={handleNewTreatmentChange}>
                      {['IV','SC','IM','PO','IN','Tópico','Rectal','CRI'].map(v=><MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 2
                    }}>
                    <TextField size="small" fullWidth select label="Frecuencia" name="frecuencia"
                      value={newTreatment.frecuencia} onChange={handleNewTreatmentChange}>
                      {['c/4h','c/6h','c/8h','c/12h','c/24h','SOS','CRI','Única dosis'].map(v=><MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 2
                    }}>
                    <TextField size="small" fullWidth label="Hora inicio" name="horaInicio" type="time"
                      value={newTreatment.horaInicio || ''} onChange={handleNewTreatmentChange}
                      helperText={newTreatment.horaInicio ? '' : 'Primera dosis'}
                      InputLabelProps={{ shrink: true }}/>
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 2
                    }}>
                    <TextField size="small" fullWidth label="Duración (días)" name="duracionDias" type="number"
                      value={newTreatment.duracionDias || ''} onChange={handleNewTreatmentChange}
                      helperText="Tiempo total de indicación"
                      InputLabelProps={{ shrink: true }}/>
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 2
                    }}>
                    <TextField size="small" fullWidth label="Dosis (mg/kg)" name="doseMgPerKg"
                      type="number" value={newTreatment.doseMgPerKg} onChange={handleNewTreatmentChange}
                      helperText={newTreatment.doseMgPerKg && weightNumber ? `→ ${(newTreatment.doseMgPerKg*weightNumber).toFixed(1)} mg` : ''}/>
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 2
                    }}>
                    <TextField size="small" fullWidth select label="Prioridad" name="prioridad"
                      value={newTreatment.prioridad} onChange={handleNewTreatmentChange}>
                      <MenuItem value="critico">🔴 Crítico</MenuItem>
                      <MenuItem value="rutina">🟡 Rutina</MenuItem>
                      <MenuItem value="sos">🟢 SOS</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField size="small" fullWidth label="Instrucción especial" name="notas"
                      value={newTreatment.notas} onChange={handleNewTreatmentChange}/>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 4
                    }}>
                    <Autocomplete size="small" options={medicalUsers}
                      value={medicalUsers.find(u => u.id === newTreatment.doctorId) || null}
                      getOptionLabel={u=>`${u.name} ${u.lastName||''}`.trim()}
                      onChange={(_,v)=>setNewTreatment(p=>({...p,doctorId:v?.id||''}))}
                      renderInput={p=><TextField {...p} label="Médico que indica"/>}/>
                  </Grid>
                  <Grid
                    sx={{ display:'flex', alignItems:'flex-start', flexDirection:'column' }}
                    size={{
                      xs: 12,
                      sm: 2
                    }}>
                    <Button variant="contained" fullWidth onClick={handleAddTreatment}
                      sx={{ bgcolor:PRIMARY, height:40 }} startIcon={<AddIcon/>}>
                      {editingId && editingType === 'medication' ? 'Guardar cambios' : 'Agregar'}
                    </Button>
                    {editingId && editingType === 'medication' && (
                      <Button size="small" fullWidth onClick={handleCancelEdit} sx={{ mt: 0.5 }}>Cancelar</Button>
                    )}
                  </Grid>
                </Grid>
              )}

              {indicationTab === 1 && (
                <Grid container spacing={1.5}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 5
                    }}>
                    <Autocomplete size="small" options={procedures}
                      getOptionLabel={o=>o.procedure||''}
                      onChange={(_,v)=>handleNewProcedureChange('procedureId',v?.id||'')}
                      filterOptions={(opts,{inputValue})=>opts.filter(o=>o.procedure?.toLowerCase().includes(inputValue.toLowerCase()))}
                      renderInput={p=><TextField {...p} label="Procedimiento"/>}/>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 4
                    }}>
                    <TextField size="small" fullWidth label="Notas" value={newProcedure.notes}
                      onChange={e=>handleNewProcedureChange('notes',e.target.value)}/>
                  </Grid>
                  <Grid
                    sx={{ display:'flex', alignItems:'flex-start', flexDirection:'column' }}
                    size={{
                      xs: 12,
                      sm: 3
                    }}>
                    <Button variant="contained" fullWidth onClick={handleAddProcedure}
                      sx={{ bgcolor:PRIMARY, height:40 }} startIcon={<AddIcon/>}>
                      {editingId && editingType === 'procedure' ? 'Guardar cambios' : 'Agregar'}
                    </Button>
                    {editingId && editingType === 'procedure' && (
                      <Button size="small" fullWidth onClick={handleCancelEdit} sx={{ mt: 0.5 }}>Cancelar</Button>
                    )}
                  </Grid>
                </Grid>
              )}

              {indicationTab === 2 && (
                <DispenseForm patientId={hospitalization?.patientId} referenceId={hospitalizationId}
                  referenceType="hospitalization" location="hospitalization"
                  onDispenseComplete={r=>setSnackbar({open:true,message:`Dispensado: ${r?.product||''}`,severity:'success'})}/>
              )}
            </Paper>

            {/* Hoja de tratamiento: fecha, navegación y próximo tratamiento */}
            <Paper elevation={0} sx={{ p:2, mb:2, border:'1px solid #E2E8F0', borderRadius:3, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Hoja de tratamiento</Typography>
                <Typography variant="h6" fontWeight={700}>{fmtDate(viewDate)}</Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={()=>setViewDate(prev=>addDays(prev,-1))}><ChevronLeftIcon/></IconButton>
                <TextField type="date" size="small" value={viewDateStr}
                  onChange={(e)=>setViewDate(new Date(`${e.target.value}T00:00:00`))}
                  InputLabelProps={{ shrink:true }} sx={{ width:150 }} />
                <IconButton size="small" onClick={()=>setViewDate(prev=>addDays(prev,1))}><ChevronRightIcon/></IconButton>
                {isTodayView && <Chip size="small" label="Hoy" color="primary" sx={{ ml:0.5 }} />}
              </Stack>
              <Box sx={{ textAlign:{ xs:'left', md:'right' } }}>
                <Typography variant="body2" color="text.secondary">Próximo tratamiento</Typography>
                <Typography fontWeight={700} color={PRIMARY}>
                  {nextTto ? `${nextTto.name} a las ${nextTto.ts.toLocaleTimeString('es-CL', { hour:'2-digit', minute:'2-digit' })}` : '—'}
                </Typography>
              </Box>
            </Paper>

            {/* Tabla de tratamientos con columna sticky */}
            <Paper elevation={0} sx={{ border:'1px solid #E2E8F0', borderRadius:3, overflowX:'auto' }}>
              {treatments.length === 0 ? (
                <Box sx={{ p:4, textAlign:'center' }}>
                  <Typography color="text.secondary">Sin tratamientos. Agrega uno arriba.</Typography>
                </Box>
              ) : (
                <Table size="small" sx={{ tableLayout:'auto', minWidth: 600 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor:'#F8FAFC' }}>
                      <TableCell sx={{ position:'sticky', left:0, zIndex:2, bgcolor:'#F8FAFC', minWidth:220, borderRight:'2px solid #E2E8F0', fontWeight:700, fontSize:'0.75rem' }}>
                        Indicación
                      </TableCell>
                      {visibleHours.map(h=>(
                        <TableCell key={h} align="center" sx={{ fontWeight:700, fontSize:'0.72rem', minWidth:70, bgcolor: turnoHoursBg(h), py:0.8 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...treatments].sort((a,b)=>{
                      const order={critico:0,rutina:1,sos:2};
                      return (order[a.prioridad]||1)-(order[b.prioridad]||1);
                    }).map(t=>(
                      <TableRow key={t.id} hover sx={{ '&:hover td':{ bgcolor:'#F1F5F9' } }}>
                        <TableCell sx={{ position:'sticky', left:0, zIndex:1, bgcolor:'white', borderRight:'2px solid #E2E8F0', py:1, minWidth:220 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Stack spacing={0.3}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography sx={{ fontSize:'0.08rem' }}>
                                  {t.prioridad==='critico'?'🔴':t.prioridad==='sos'?'🟢':'🟡'}
                                </Typography>
                                <Chip label={t.type==='medication'?'Med':'Proc'} size="small"
                                  sx={{ fontSize:'0.6rem', height:18, bgcolor: t.type==='medication'?`${PRIMARY}22`:CRI_COLOR+'22', color: t.type==='medication'?PRIMARY:CRI_COLOR }}/>
                              </Stack>
                              <Typography fontWeight={700} sx={{ fontSize:'0.78rem', lineHeight:1.2 }}>{t.name}</Typography>
                              {t.doseMgPerKg && <Typography sx={{ fontSize:'0.68rem', color:MUTED }}>{t.doseMgPerKg} mg/kg{weightNumber?` → ${(t.doseMgPerKg*weightNumber).toFixed(1)} mg`:''}</Typography>}
                              {(t.via||t.frecuencia) && <Typography sx={{ fontSize:'0.68rem', color:MUTED }}>{[t.via,t.frecuencia].filter(Boolean).join(' · ')}</Typography>}
                              {t.notas && <Typography sx={{ fontSize:'0.65rem', color:MUTED, fontStyle:'italic' }}>{t.notas}</Typography>}
                              {t.doctorName && <Typography sx={{ fontSize:'0.65rem', color:MUTED }}>{t.doctorName}</Typography>}
                              {(() => {
                                const inicio = getTreatmentStart(t);
                                const proxima = getTreatmentNext(t, hours, now);
                                const termino = getTreatmentEnd(t, hours, viewDate);
                                if (!inicio && !proxima && !termino) return null;
                                return (
                                  <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 0.3 }}>
                                    {inicio && <Typography sx={{ fontSize:'0.62rem', color:MUTED }}>Inicio: {fmtDateTime(inicio)}</Typography>}
                                    {proxima && <Typography sx={{ fontSize:'0.62rem', color:INFO }}>Próxima: {fmtDateTime(proxima)}</Typography>}
                                    {termino && <Typography sx={{ fontSize:'0.62rem', color:MUTED }}>Término: {fmtDateTime(termino)}</Typography>}
                                  </Stack>
                                );
                              })()}
                            </Stack>
                            <Stack direction="row" spacing={0.2}>
                              <Tooltip title="Editar" arrow>
                                <IconButton size="small" onClick={()=>handleEditClick(t)}><EditIcon fontSize="small"/></IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar" arrow>
                                <IconButton size="small" onClick={()=>handleDeleteClick(t.id)}><DeleteIcon fontSize="small"/></IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        </TableCell>
                        {visibleHours.map(h=>{
                          const cell = getCellForDate(t, h, viewDate, now);
                          const st = calcCellStatus(t, h, now, hours, viewDate);
                          const cs = CELL_STATES[st] || CELL_STATES.pending;
                          const tooltip = st === 'na' ? 'No programado' : (cell.executedBy ? `${cell.executedBy} ${cell.timeStr||''}` : cs.label);
                          return (
                            <TableCell key={h} align="center" sx={{ p:0.5, bgcolor: turnoHoursBg(h) }}>
                              <Tooltip title={tooltip} arrow>
                                <span>
                                  <Button size="small" onClick={()=>handleToggleCell(t.id,h)} disabled={st === 'na'}
                                    sx={{ minWidth:56, height:36, fontSize:'0.68rem', fontWeight:700,
                                      bgcolor: cs.bg, color: cs.color, border:`1px solid ${cs.color}33`,
                                      '&:hover':{ bgcolor: cs.bg, filter:'brightness(0.93)' } }}>
                                    {cs.label}
                                  </Button>
                                </span>
                              </Tooltip>
                              {cell.executedBy && <Typography sx={{ fontSize:'0.58rem', color:MUTED, display:'block', lineHeight:1 }}>{cell.executedBy.split(' ')[0]}</Typography>}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        )}

        {/* ── TAB 2: DIAGNÓSTICOS ── */}
        {activeTab === 2 && (
          <Paper elevation={0} sx={{ p:3, border:'1px solid #E2E8F0', borderRadius:3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>Diagnósticos</Typography>
            <Grid container spacing={2} mb={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Autocomplete options={diagnoses} getOptionLabel={o=>o.diagnosis||''}
                  isOptionEqualToValue={(o,v)=>o.id===v.id} value={null}
                  onChange={handlePrimaryDiagnosisChange} disabled={!!hospitalization.primaryDiagnosisId}
                  filterOptions={(opts,{inputValue})=>opts.filter(o=>o.diagnosis?.toLowerCase().includes(inputValue.toLowerCase()))}
                  renderInput={p=><TextField {...p} label="Diagnóstico principal" size="small"/>}/>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Autocomplete options={diagnoses} getOptionLabel={o=>o.diagnosis||''}
                  isOptionEqualToValue={(o,v)=>o.id===v.id} value={null}
                  onChange={handleSecondaryDiagnosisChange}
                  filterOptions={(opts,{inputValue})=>opts.filter(o=>o.diagnosis?.toLowerCase().includes(inputValue.toLowerCase()))}
                  renderInput={p=><TextField {...p} label="Diagnóstico secundario" size="small"/>}/>
              </Grid>
            </Grid>
            <Stack spacing={1}>
              {hospitalization.primaryDiagnosis && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth:100 }}>Principal:</Typography>
                  <Chip label={hospitalization.primaryDiagnosis} onDelete={handleRemovePrimaryDiagnosis}
                    sx={{ bgcolor:`${PRIMARY}15`, color:PRIMARY, fontWeight:600 }}/>
                </Stack>
              )}
              {(hospitalization.secondaryDiagnoses||[]).length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth:100 }}>Secundarios:</Typography>
                  {(hospitalization.secondaryDiagnoses||[]).map((d,i)=>(
                    <Chip key={i} label={d} onDelete={()=>handleRemoveSecondaryDiagnosis(i)} size="small" variant="outlined"/>
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
        )}

        {/* ── TAB 3: EVOLUCIÓN SOAP ── */}
        {activeTab === 3 && (
          <Box>
            <Stack direction="row" justifyContent="flex-end" mb={2}>
              <Button variant="contained" startIcon={<AddIcon/>} onClick={()=>{ setEditingSOAP(null); setSoapDlg(true); }}
                sx={{ bgcolor:PRIMARY }}>Nuevo SOAP del día</Button>
            </Stack>
            {(hospitalization.soapDiario||[]).length === 0 ? (
              <Paper elevation={0} sx={{ p:4, textAlign:'center', border:'1px solid #E2E8F0', borderRadius:3 }}>
                <Typography color="text.secondary">Sin evoluciones SOAP registradas.</Typography>
              </Paper>
            ) : [...(hospitalization.soapDiario||[])].sort((a,b)=>(b.timestamp?.seconds||0)-(a.timestamp?.seconds||0)).map(s=>(
              <Accordion key={s.id} sx={{ mb:1, border:'1px solid #E2E8F0', borderRadius:'12px!important', '&:before':{ display:'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flex:1, pr:1 }}>
                    <Typography fontWeight={700} sx={{ fontSize:'0.9rem' }}>{fmtDate(s.fecha||s.timestamp)}</Typography>
                    {s.firmadoPor && <Chip label={s.firmadoPor} size="small" variant="outlined"/>}
                    <Box sx={{ ml:'auto' }} onClick={e=>e.stopPropagation()}>
                      <Tooltip title="Editar"><IconButton size="small" onClick={()=>{ setEditingSOAP(s); setSoapDlg(true); }}><EditIcon fontSize="small"/></IconButton></Tooltip>
                      <Tooltip title="Eliminar"><IconButton size="small" onClick={()=>{ if(window.confirm('¿Eliminar este SOAP?')) handleDeleteSOAP(s.id); }}><DeleteIcon fontSize="small"/></IconButton></Tooltip>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {[['S — Subjetivo',s.subjetivo],['O — Objetivo',s.objetivo],['A — Evaluación',s.evaluacion],['P — Plan',s.plan]].map(([lbl,val])=>(
                      <Grid
                        key={lbl}
                        size={{
                          xs: 12,
                          sm: 6
                        }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">{lbl}</Typography>
                        <Typography variant="body2" sx={{ mt:0.5, whiteSpace:'pre-wrap' }}>{val||'–'}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* ── TAB 4: ALTA ── */}
        {activeTab === 4 && (
          <Paper elevation={0} sx={{ p:3, border:'1px solid #E2E8F0', borderRadius:3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>Protocolo de Alta</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Completa el checklist para habilitar el alta del paciente.
            </Typography>
            <Button variant="contained" color="warning" startIcon={<ExitToAppIcon/>}
              onClick={()=>setDischargeDlg(true)}>
              Iniciar protocolo de alta
            </Button>
            {(hospitalization.notasTurno||[]).length > 0 && (
              <Box mt={4}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={700}>Notas de turno</Typography>
                  <Button size="small" startIcon={<AddIcon/>} onClick={()=>{ setEditingShiftNote(null); setShiftDlg(true); }}
                    sx={{ fontSize:'0.75rem' }}>Nueva nota</Button>
                </Stack>
                {[...(hospitalization.notasTurno||[])].sort((a,b)=>(b.timestamp?.seconds||0)-(a.timestamp?.seconds||0)).map(n=>(
                  <Paper key={n.id} elevation={0} sx={{ p:2, mb:1, border:'1px solid #E2E8F0', borderRadius:2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      {n.turno==='mañana'?<WbSunnyIcon sx={{fontSize:16,color:AMBER}}/>:n.turno==='noche'?<NightlightIcon sx={{fontSize:16,color:CRI_COLOR}}/>:<WbSunnyIcon sx={{fontSize:16,color:INFO}}/>}
                      <Typography variant="caption" fontWeight={700} textTransform="capitalize">{n.turno}</Typography>
                      <Typography variant="caption" color="text.secondary">{fmtDateTime(n.timestamp)}</Typography>
                      <Typography variant="caption" color="text.secondary">· {n.registradoPor}</Typography>
                      <Box sx={{ ml:'auto' }}>
                        <Tooltip title="Editar"><IconButton size="small" onClick={()=>{ setEditingShiftNote(n); setShiftDlg(true); }}><EditIcon fontSize="small"/></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" onClick={()=>{ if(window.confirm('¿Eliminar esta nota de turno?')) handleDeleteShiftNote(n.id); }}><DeleteIcon fontSize="small"/></IconButton></Tooltip>
                      </Box>
                    </Stack>
                    {n.observaciones && <Typography variant="body2" sx={{ whiteSpace:'pre-wrap' }}>{n.observaciones}</Typography>}
                    {n.alertasParaSiguienteTurno && (
                      <Box sx={{ mt:1, p:1, bgcolor:'#FFFBEB', borderRadius:1, border:'1px solid #FDE68A' }}>
                        <Typography variant="caption" fontWeight={700}>⚠ Alerta: </Typography>
                        <Typography variant="caption">{n.alertasParaSiguienteTurno}</Typography>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            {/* Historial de cambios de estado clínico */}
            {(hospitalization.estadoHistorial||[]).length > 0 && (
              <Box mt={4}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>Historial de cambios de estado</Typography>
                {[...(hospitalization.estadoHistorial||[])].sort((a,b)=>(b.changedAt?.seconds||0)-(a.changedAt?.seconds||0)).map(h=>{
                  const fromOpt = ESTADO_OPTIONS.find(o=>o.key===h.from);
                  const toOpt = ESTADO_OPTIONS.find(o=>o.key===h.to);
                  return (
                    <Paper key={h.id} elevation={0} sx={{ p:1.5, mb:1, border:'1px solid #E2E8F0', borderRadius:2 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary">{fmtDateTime(h.changedAt)}</Typography>
                        {fromOpt && <Chip label={fromOpt.label} size="small" sx={{ fontSize:'0.65rem', bgcolor:`${fromOpt.color}15`, color:fromOpt.color }}/>}
                        <Typography variant="caption" color="text.secondary">→</Typography>
                        {toOpt && <Chip label={toOpt.label} size="small" sx={{ fontSize:'0.65rem', bgcolor:`${toOpt.color}15`, color:toOpt.color, fontWeight:700 }}/>}
                        {h.changedBy && <Typography variant="caption" color="text.secondary">· {h.changedBy}</Typography>}
                      </Stack>
                      {h.nota && <Typography variant="body2" sx={{ mt:0.5, whiteSpace:'pre-wrap' }}>{h.nota}</Typography>}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Paper>
        )}

        {/* ── TAB 5: NOTAS DE TURNO ── */}
        {activeTab === 5 && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight={700}>Notas de turno</Typography>
              <Button size="small" startIcon={<AddIcon/>} variant="contained" onClick={()=>{ setEditingShiftNote(null); setShiftDlg(true); }} sx={{ bgcolor:PRIMARY, fontSize:'0.75rem' }}>Nueva nota</Button>
            </Stack>
            {(hospitalization.notasTurno||[]).length === 0 ? (
              <Paper elevation={0} sx={{ p:4, textAlign:'center', border:'1px solid #E2E8F0', borderRadius:3 }}>
                <Typography color="text.secondary">Sin notas de turno registradas.</Typography>
              </Paper>
            ) : [...(hospitalization.notasTurno||[])].sort((a,b)=>(b.timestamp?.seconds||0)-(a.timestamp?.seconds||0)).map(n=>{
              const hasAlert = !!n.alertasParaSiguienteTurno;
              return (
                <Paper key={n.id} elevation={0} sx={{ p:2, mb:1, border:'1px solid #E2E8F0', borderRadius:2, borderLeft: hasAlert ? `4px solid ${AMBER}` : undefined }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                    {n.turno==='mañana'?<WbSunnyIcon sx={{fontSize:16,color:AMBER}}/>:n.turno==='noche'?<NightlightIcon sx={{fontSize:16,color:CRI_COLOR}}/>:<WbSunnyIcon sx={{fontSize:16,color:INFO}}/>}
                    <Typography variant="caption" fontWeight={700} textTransform="capitalize">{n.turno}</Typography>
                    <Typography variant="caption" color="text.secondary">{fmtDateTime(n.timestamp)}</Typography>
                    <Typography variant="caption" color="text.secondary">· {n.registradoPor}</Typography>
                    <Box sx={{ ml:'auto' }}>
                      <Tooltip title="Editar"><IconButton size="small" onClick={()=>{ setEditingShiftNote(n); setShiftDlg(true); }}><EditIcon fontSize="small"/></IconButton></Tooltip>
                      <Tooltip title="Eliminar"><IconButton size="small" onClick={()=>{ if(window.confirm('¿Eliminar esta nota de turno?')) handleDeleteShiftNote(n.id); }}><DeleteIcon fontSize="small"/></IconButton></Tooltip>
                    </Box>
                  </Stack>
                  {n.observaciones && <Typography variant="body2" sx={{ whiteSpace:'pre-wrap' }}>{n.observaciones}</Typography>}
                  {hasAlert && (
                    <Box sx={{ mt:1, p:1, bgcolor:'#FFFBEB', borderRadius:1, border:'1px solid #FDE68A' }}>
                      <Typography variant="caption" fontWeight={700}>⚠ Alerta: </Typography>
                      <Typography variant="caption">{n.alertasParaSiguienteTurno}</Typography>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}

        {/* ── TAB 6: BALANCE ── */}
        {activeTab === 6 && (
          <BalancePanel hosp={hospitalization} users={medicalUsers} onSave={handleSaveBalance}/>
        )}
      </Box>
      {/* Dialogs */}
      <CellMarkDialog open={cellDlg.open} treatment={cellDlg.treatment} hour={cellDlg.hour}
        users={medicalUsers} onClose={handleCellConfirm}/>
      <ShiftNotesDialog open={shiftDlg} onClose={()=>{ setShiftDlg(false); setEditingShiftNote(null); }} users={medicalUsers} onSave={handleSaveShiftNote} initialData={editingShiftNote}/>
      <DischargeDialog open={dischargeDlg} onClose={()=>setDischargeDlg(false)} onDischarge={handleDischarge} hospitalizationId={hospitalizationId}/>
      <DailySOAPDialog open={soapDlg} onClose={()=>{ setSoapDlg(false); setEditingSOAP(null); }} users={medicalUsers} onSave={handleSaveSOAP} initialData={editingSOAP}/>
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={()=>setSnackbar(p=>({...p,open:false}))}
        anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity={snackbar.severity} onClose={()=>setSnackbar(p=>({...p,open:false}))} sx={{ width:'100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HospitalFlowsheet;
